import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Skeleton,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  TextField,
  IconButton,
  CircularProgress,
  Paper,
  Stack,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useMutation } from "react-query";
import { chatDashboardMetric } from "../api/client";

const defaultTitleFromMetric = (metric) => {
  if (metric === "tme") return "TME (média) — IA";
  if (metric === "tma") return "TMA (média) — IA";
  return metric ? "Métrica — IA" : "Métrica — IA";
};

const defaultChipFromMetric = (metric) => {
  if (metric === "tme") return "Tempo médio de espera";
  if (metric === "tma") return "Tempo médio de atendimento";
  return "Métrica";
};

const fmtBrDate = (value) => {
  if (!value) return "";
  const s = String(value).trim();
  // ISO curto: YYYY-MM-DD -> DD/MM/YYYY
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  // Tenta parsear datas completas (ISO datetime etc.)
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("pt-BR");
  return s;
};

const MetricAiInsightsDialog = ({
  open,
  metric,
  metricName,
  metricLabel,
  metricHuman,
  period,
  loading,
  error,
  data,
  onClose,
  // basePayload = { metric, metricName, metricDefinition, metricHuman, period, filters, summaryContext, ... }
  scope,
  basePayload,
}) => {
  const title = (metricName && `${metricName} — IA`) || defaultTitleFromMetric(metric);
  const chipLabel = metricLabel || defaultChipFromMetric(metric);

  const [chatMessages, setChatMessages] = React.useState([]); // {role:"user"|"assistant", content:string}[]
  const [input, setInput] = React.useState("");
  const [sessionId, setSessionId] = React.useState("");
  const listRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    // reseta conversa ao abrir um novo tópico
    setChatMessages([]);
    setInput("");
    setSessionId("");
  }, [open, metric, metricName]);

  React.useEffect(() => {
    if (!open) return;
    const sid = data?.sessionId;
    if (sid && typeof sid === "string") setSessionId(sid);
  }, [open, data]);

  React.useEffect(() => {
    // auto-scroll quando chega mensagem
    if (!listRef.current) return;
    try {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    } catch {}
  }, [chatMessages, loading]);

  const chatMutation = useMutation(
    async ({ userMessage, history }) => {
      const payload = {
        ...(basePayload || {}),
        metric,
        metricName,
        metricLabel: chipLabel,
        metricHuman,
        period,
        sessionId: sessionId || undefined,
        userMessage,
        history,
      };
      return chatDashboardMetric(scope, payload);
    },
    {}
  );

  const handleSend = async () => {
    const msg = String(input || "").trim();
    if (!msg || chatMutation.isLoading) return;

    const next = [...chatMessages, { role: "user", content: msg }];
    setChatMessages(next);
    setInput("");

    try {
      const history = next.slice(-10); // limita contexto (segurança/custo)
      const resp = await chatMutation.mutateAsync({ userMessage: msg, history });
      const reply = String(resp?.reply || resp?.message || "").trim() || "Não consegui responder no momento.";
      setChatMessages((cur) => [...cur, { role: "assistant", content: reply }]);
    } catch (e) {
      const emsg =
        (typeof e?.message === "string" && e.message) ||
        (typeof e?.error === "string" && e.error) ||
        "Não foi possível conversar com a IA no momento.";
      setChatMessages((cur) => [...cur, { role: "assistant", content: emsg }]);
    }
  };

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          height: { xs: "78vh", md: "72vh" },
          overflow: "hidden", // evita scroll do popup; cada painel gerencia seu próprio scroll
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: "1 1 auto", minHeight: 0 }}>
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            Este recurso é experimental. A IA pode errar — valide as informações antes de tomar decisões.
          </Alert>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            <Chip
              size="small"
              label={chipLabel}
              variant="outlined"
            />
            {metricHuman && (
              <Chip size="small" label={`Atual: ${metricHuman}`} color="primary" variant="outlined" />
            )}
            {period?.start && period?.end && (
              <Chip
                size="small"
                label={`Período: ${fmtBrDate(period.start)} → ${fmtBrDate(period.end)}`}
                variant="outlined"
              />
            )}
          </Box>

          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
              <CircularProgress size={18} />
              <Typography variant="body2">
                Estou analisando seus dados para te explicar isso de forma simples…
              </Typography>
            </Box>
          )}

          {!loading && error && (
            <Alert severity="warning" variant="outlined">
              {error}
            </Alert>
          )}

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            sx={{ flex: "1 1 auto", minHeight: 0 }}
          >
            {/* Resumo primeiro (objetivo principal) */}
            <Box
              sx={{
                flex: { xs: "1 1 55%", md: 1 },
                minWidth: 0,
                minHeight: 0,
                overflow: "auto",
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                Resumo da IA (leitura rápida)
              </Typography>

              {loading && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Skeleton variant="rounded" width="100%" height={90} />
                  <Skeleton variant="rounded" width="100%" height={90} />
                  <Skeleton variant="rounded" width="100%" height={120} />
                </Box>
              )}

              {!loading && !error && !data && (
                <Typography variant="body2" color="text.secondary">
                  A explicação inicial aparece aqui em instantes.
                </Typography>
              )}

              {!loading && !error && data && (
                <Stack spacing={1.25}>
                  {data?.explanation && (
                    <Paper variant="outlined" sx={{ p: 1.25 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        O que esse número significa
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                        {data.explanation}
                      </Typography>
                    </Paper>
                  )}

                  {data?.impact && (
                    <Paper variant="outlined" sx={{ p: 1.25 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        Impacto nos resultados
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                        {data.impact}
                      </Typography>
                    </Paper>
                  )}

                  {(Array.isArray(data?.suggestions) && data.suggestions.length > 0) && (
                    <Paper variant="outlined" sx={{ p: 1.25 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        Sugestões práticas
                      </Typography>
                      <List dense sx={{ m: 0 }}>
                        {data.suggestions.map((s, idx) => (
                          <ListItem key={idx} sx={{ py: 0.25, px: 0 }}>
                            <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={s} />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}

                  {(Array.isArray(data?.quickWins) && data.quickWins.length > 0) && (
                    <Paper variant="outlined" sx={{ p: 1.25 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        Quick wins (hoje)
                      </Typography>
                      <List dense sx={{ m: 0 }}>
                        {data.quickWins.map((s, idx) => (
                          <ListItem key={idx} sx={{ py: 0.25, px: 0 }}>
                            <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={s} />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Stack>
              )}
            </Box>

            {/* Chat (lado direito no desktop / abaixo no mobile) */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.25,
                flex: { xs: "1 1 auto", md: "0 0 360px" },
                width: { xs: "100%", md: 360 },
                minHeight: { xs: 260, md: 0 },
                maxHeight: { xs: "45%", md: "100%" },
                display: "flex",
                flexDirection: "column",
                gap: 1,
                overflow: "hidden",
              }}
            >
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={1}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Tire dúvidas
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Somente este assunto
                </Typography>
              </Stack>

              <Box
                ref={listRef}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1,
                  flex: "1 1 auto",
                  minHeight: 0,
                  overflow: "auto",
                  bgcolor: "background.paper",
                }}
              >
                {chatMessages.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Ex.: “O que pode estar aumentando esse número?” ou “Quais ações priorizar esta semana?”
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {chatMessages.map((m, idx) => (
                      <Box key={idx} sx={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "92%" }}>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.75,
                            borderRadius: 1,
                            bgcolor: m.role === "user" ? "primary.main" : "grey.100",
                            color: m.role === "user" ? "primary.contrastText" : "text.primary",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          <Typography variant="body2">{m.content}</Typography>
                        </Box>
                      </Box>
                    ))}
                    {chatMutation.isLoading && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">
                          Estou analisando e preparando uma resposta…
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  pt: 0.5,
                  bgcolor: "background.paper",
                  mt: "auto",
                  flexShrink: 0,
                }}
              >
                <TextField
                  size="small"
                  fullWidth
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Pergunte sobre essa métrica…"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
                <IconButton color="primary" onClick={handleSend} disabled={!input.trim() || chatMutation.isLoading}>
                  <SendIcon />
                </IconButton>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MetricAiInsightsDialog;

